import {createContext, ReactNode, useContext, useState} from 'react'
import {SosPublicationType} from "../components/types";
import {useSessionContext} from "./SessionContext";

export interface CartContextType {
    addToCart: (item: SosPublicationType) => void,
    cartTotal: number,
    cartTotalDelivery: number,
    clearCart: () => void,
    decreaseItemQuantity: (itemToDecrease: CartItem) => void,
    getCartItems: () => CartItem[],
    getLatestAddedItem: () => CartItem | null,
    increaseItemQuantity: (itemToIncrease: CartItem) => void,
    isCartFilled: boolean,
    removeFromCart: (itemToRemove: CartItem) => void,
    totalAmountOfCartItems: number,
}

const CartContext = createContext<CartContextType>({} as CartContextType)
const useCartContext = () => useContext(CartContext)

interface Props {
    children: ReactNode
}


export type CartItem = {
    sosPublication: SosPublicationType
    quantity: number
    price: number
}


const CartProvider = (props: Props) => {
    const session = useSessionContext()

    const [cartItems, _setCartItems] = useState<CartItem[]>([])
    const [lastAddedItem, setLastAddedItem] = useState<CartItem | null>(null)


    const isCartFilled = session.getJsonFromSession("cart") as CartItem[] != null && (session.getJsonFromSession("cart") as CartItem[]).length > 0;

    const setCartItems = (items: CartItem[]) => {
        _setCartItems(items);
        session.addJsonToSession("cart", items)
    }
    const getCartItems = () => session.getJsonFromSession("cart") as CartItem[];
    const getLatestAddedItem = () => lastAddedItem;

    const totalAmountOfCartItems = getCartItems()?.reduce(
        (acc, {quantity}) => acc + quantity, 0
    );

    const clearCart = () => {
        _setCartItems([])
        session.removeFromSession("cart")
    }

    function calculateCartTotal() {
        const items = getCartItems();
        let total = 0;
        if (items) {
            items.forEach(item => {
                total += item.quantity * item.price
            })
        }
        return total
    }

    function calculateCartTotalDelivery() {
        let total = calculateCartTotal();
        if (total && total > 0) {
            total += 2;
        }
        return total;
    }

    const addToCart = (item: SosPublicationType) => {
        const newItem: CartItem = {
            sosPublication: item,
            quantity: 1,
            price: item.charge ? parseFloat(item.charge!.replace(',', '.')) : 0.00
        }
        if (isNaN(newItem.price)) {
            newItem.price = 0
        }
        let increased = false
        cartItems.map(item => {
            if (item.sosPublication.id === newItem.sosPublication.id) {
                increaseItemQuantity(newItem)
                increased = true
            }
        })
        if (!increased) {
            setCartItems([...cartItems, newItem])
        }

        setLastAddedItem(newItem);
    }

    const removeFromCart = (itemToRemove: CartItem) => {
        setCartItems(cartItems.filter(item => item.sosPublication.id !== itemToRemove.sosPublication.id))
    }

    const increaseItemQuantity = (itemToIncrease: CartItem) => {
        const updatedCartItems = cartItems.map(item => {
            if (item.sosPublication.id === itemToIncrease.sosPublication.id) {
                return {...item, quantity: item.quantity + 1}
            }
            return item
        })
        setCartItems(updatedCartItems)
    }

    const decreaseItemQuantity = (itemToDecrease: CartItem) => {
        const updatedCartItems = cartItems.map(item => {
            if (item.sosPublication.id === itemToDecrease.sosPublication.id) {
                return {...item, quantity: item.quantity - 1}
            }
            return item
        })
        setCartItems(updatedCartItems.filter(item => item.quantity > 0))
    }

    const cartTotal = calculateCartTotal()
    const cartTotalDelivery = calculateCartTotalDelivery()


    return (
        <CartContext.Provider
            value={{clearCart, isCartFilled, getCartItems, totalAmountOfCartItems, addToCart, cartTotal, removeFromCart, increaseItemQuantity, decreaseItemQuantity, cartTotalDelivery, getLatestAddedItem}}>
            {props.children}
        </CartContext.Provider>
    )
}


export {
    useCartContext,
    CartProvider
}